/*eslint-disable*/
/**
 * CalendarInput Component
 * 
 * A flexible calendar input component that supports:
 * - Single date selection
 * - Week selection
 * - Date range selection
 * - Time selection (when includeTime is true and not in week/range mode)
 * 
 * Usage examples:
 * 
 * // Basic single date picker
 * <CalendarInput onChange={handleDate} />
 * 
 * // Single date picker with time selection
 * <CalendarInput includeTime onChange={handleDateTime} />
 * 
 * // Week picker (time selection ignored)
 * <CalendarInput week onChange={handleWeek} />
 * 
 * // Date range picker (time selection ignored)
 * <CalendarInput range onChange={handleRange} />
 */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { BodyText, OtherText } from 'styling/Typography'
import { InputWrapper, Label } from 'components/Input/Input.styled'
import { months } from 'utils/helpers'
import { Display, Grid, Header, NavButton, Popup, Selects, WeekdaysRow, Wrapper, DayCell, TimePickerSection, TimePickerGroup, TimePickerLabel, TimePickerSelect, Footer } from './CalendarInput.styled'
import { createPortal } from 'react-dom'
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react'
import { Flex } from 'styling/GlobalStyles'
import { CalendarIcon } from './CalendarIcon'
import { Placeholder } from 'components/HonestSelect/HonestSelect.styled'
import { openStdin } from 'process'

type OnChangeSingle = (date: Date | null, name?: string) => void
type OnChangeRange = (firstDay: Date, lastDay: Date, name?: string) => void

type ValueDateLike = Date | string | number
/** Represents a range with explicit keys. */
export type ValueRangeObject = { firstDay: ValueDateLike; lastDay: ValueDateLike }
/** Represents a range as a tuple: [firstDay, lastDay]. */
export type ValueRangeTuple = [ValueDateLike, ValueDateLike]
/** Allowed defaultValue types across all modes. */
export type DefaultValue = ValueDateLike | null | ValueRangeObject | ValueRangeTuple

export interface CalendarInputProps {
    /** Label displayed above the input, same API as Input.tsx. */
    labelText?: string
    hideIcon?: boolean
    /** Optional field name to be forwarded in onChange as the last argument. */
    name?: string
    /**
     * Initial value used to set the starting state of the calendar. Not controlled.
     * - Single mode (no week/range): Date or null
     * - Week mode: Date (any day in the week) OR { firstDay, lastDay } OR [firstDay, lastDay]
     * - Range mode: { firstDay, lastDay } OR [firstDay, lastDay] OR Date (treated as first selection)
     */
    defaultValue?: DefaultValue
    /**
     * Change handler called only after user interaction:
     * - Single mode: (date: Date | null) => void
     * - Week/Range mode: (firstDay: Date, lastDay: Date) => void
     */
    onChange?: OnChangeSingle | OnChangeRange
    /** Smallest selectable date (inclusive). */
    minDate?: Date
    /** Largest selectable date (inclusive). */
    maxDate?: Date
    /** Specific dates to disable (exact day match). */
    disabledDates?: Date[]
    /** Week picker mode: selects and returns an entire week. */
    week?: boolean
    /** Range picker mode: selects and returns a start and end date. */
    range?: boolean
    /** Placeholder text shown when nothing is selected. */
    placeholder?: string
    /** Component width (e.g., '100%', '280px'). */
    width?: string
    /** If true, popup uses fixed positioning; otherwise absolute. */
    fixedPopup?: boolean
    /** If true, shows time picker for single day selection (ignored for week/range modes). */
    includeTime?: boolean

    nomarg?: boolean

    required?: boolean
}

const START_WEEK_DAY = 1 // Monday


const sameDay = (a?: Date | null, b?: Date | null) => {
    if (!a || !b) return false
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

const isBetween = (day: Date, start?: Date | null, end?: Date | null) => {
    if (!start || !end) return false
    const d = day.setHours(0, 0, 0, 0)
    const s = start.setHours(0, 0, 0, 0)
    const e = end.setHours(0, 0, 0, 0)
    return d >= Math.min(s, e) && d <= Math.max(s, e)
}

const isDisabled = (day: Date, minDate?: Date, maxDate?: Date, disabledDates?: Date[]) => {
    const normalize = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
    const time = normalize(day)
    if (minDate && time < normalize(minDate)) return true
    if (maxDate && time > normalize(maxDate)) return true
    if (disabledDates && disabledDates.some(d => sameDay(d, day))) return true
    return false
}

const getMonthMatrix = (year: number, month: number) => {
    const firstOfMonth = new Date(year, month, 1)
    const firstDayIndex = (firstOfMonth.getDay() + 7 - START_WEEK_DAY) % 7
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const prevMonthLastDate = new Date(year, month, 0).getDate()
    const grid: Date[] = []

    // Leading days from previous month to fill the first week
    for (let i = firstDayIndex - 1; i >= 0; i--) {
        const day = prevMonthLastDate - i
        grid.push(new Date(year, month - 1, day))
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) grid.push(new Date(year, month, d))

    // Trailing days from next month to complete the last week
    let nextDay = 1
    while (grid.length % 7 !== 0) {
        grid.push(new Date(year, month + 1, nextDay++))
    }

    return grid
}

const getWeekRange = (date: Date) => {
    const d = new Date(date)
    const day = (d.getDay() + 7 - START_WEEK_DAY) % 7
    const first = new Date(d)
    first.setDate(d.getDate() - day)
    const last = new Date(first)
    last.setDate(first.getDate() + 6)
    first.setHours(0, 0, 0, 0)
    last.setHours(23, 59, 59, 999)
    return { first, last }
}

const normalizeDateForSubmission = (date: Date, options?: { preserveTime?: boolean }) => {
    // If preserveTime is true, keep the time as selected; otherwise, set to local noon to avoid UTC day shifts
    if (options?.preserveTime) {
        const d = new Date(date)
        d.setSeconds(0, 0)
        return d
    }
    // Use 12:00 local time to ensure UTC conversion does not flip the date
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0)
}

//TODO invalid
export const CalendarInput: React.FC<CalendarInputProps> = ({
    defaultValue,
    onChange,
    minDate,
    maxDate,
    disabledDates,
    week,
    range,
    placeholder,
    width,
    labelText,
    name,
    includeTime,
    nomarg,
    required,
    hideIcon
}) => {
    const [open, setOpen] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const asDate = (v: any): Date | null => {
        if (!v && v !== 0) return null
        if (v instanceof Date) return isNaN(v.getTime()) ? null : v
        const d = new Date(v)
        return isNaN(d.getTime()) ? null : d
    }

    // Time picker state (only used when includeTime is true and not week/range mode)
    const [selectedHour, setSelectedHour] = useState<number>(() => {
        if (includeTime && !week && !range && defaultValue) {
            const date = asDate(defaultValue)
            return date ? date.getHours() : 0
        }
        return 0
    })
    const [selectedMinute, setSelectedMinute] = useState<number>(() => {
        if (includeTime && !week && !range && defaultValue) {
            const date = asDate(defaultValue)
            return date ? date.getMinutes() : 0
        }
        return 0
    })

    const { refs, x, y, strategy, update } = useFloating({
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        strategy: 'fixed',
    })

    // Close on outside click, but ignore clicks on the reference and floating elements
    useEffect(() => {
        if (!open) return
        const handleDown = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node
            const refEl = refs.reference?.current as unknown as Node | null
            const floatEl = refs.floating?.current as unknown as Node | null
            if (refEl && refEl.contains(target)) return
            if (floatEl && floatEl.contains(target)) return
            setOpen(false)
        }
        document.addEventListener('mousedown', handleDown)
        document.addEventListener('touchstart', handleDown)
        return () => {
            document.removeEventListener('mousedown', handleDown)
            document.removeEventListener('touchstart', handleDown)
        }
    }, [open, refs.reference, refs.floating])

    useEffect(() => {
        if (open) update?.()
    }, [open, update])



    const initialFromDefault = (() => {
        if (Array.isArray(defaultValue) && defaultValue.length === 2) {
            return asDate(defaultValue[0]) || asDate(defaultValue[1])
        }
        if (defaultValue && typeof defaultValue === 'object' && !(defaultValue instanceof Date)) {
            const dv = defaultValue as ValueRangeObject
            return asDate(dv.firstDay) || asDate(dv.lastDay)
        }
        return asDate(defaultValue)
    })()

    // ---- Initialization helpers ----
    const anchorForInit = useMemo(() => {
        return (initialFromDefault ?? maxDate ?? minDate ?? new Date())
    }, [initialFromDefault, maxDate, minDate])

    const [viewYear, setViewYear] = useState<number>(anchorForInit.getFullYear())
    const [viewMonth, setViewMonth] = useState<number>(anchorForInit.getMonth())

    // If constraints change, clamp current view into range
    useEffect(() => {
        if (!minDate && !maxDate) return
        const currentView = new Date(viewYear, viewMonth, 1)
        if (minDate && currentView < new Date(minDate.getFullYear(), minDate.getMonth(), 1)) {
            setViewYear(minDate.getFullYear())
            setViewMonth(minDate.getMonth())
        } else if (maxDate && currentView > new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) {
            setViewYear(maxDate.getFullYear())
            setViewMonth(maxDate.getMonth())
        }
    }, [minDate, maxDate])

    const [start, setStart] = useState<Date | null>(() => {
        if (week || range) {
            if (Array.isArray(defaultValue) && defaultValue.length === 2) return asDate(defaultValue[0])
            if (defaultValue && typeof defaultValue === 'object' && !(defaultValue instanceof Date)) return asDate((defaultValue as ValueRangeObject).firstDay)
            const d = asDate(defaultValue)
            if (d) {
                if (week) return getWeekRange(d).first
                return d
            }
            return null
        }
        return asDate(defaultValue)
    })
    const [end, setEnd] = useState<Date | null>(() => {
        if (week || range) {
            if (Array.isArray(defaultValue) && defaultValue.length === 2) return asDate(defaultValue[1])
            if (defaultValue && typeof defaultValue === 'object' && !(defaultValue instanceof Date)) return asDate((defaultValue as ValueRangeObject).lastDay)
            const d = asDate(defaultValue)
            if (d && week) return getWeekRange(d).last
        }
        return null
    })
    const [selectedDate, setSelectedDate] = useState<Date | null>(() => (!week && !range ? asDate(defaultValue) : null))

    // Sync when defaultValue changes
    useEffect(() => {
        const d0 = asDate(defaultValue)
        if (week) {
            if (Array.isArray(defaultValue) && defaultValue.length === 2) {
                const a = asDate(defaultValue[0])
                const b = asDate(defaultValue[1])
                setStart(a ?? null)
                setEnd(b ?? null)
                const anchor = a ?? b
                if (anchor) { setViewYear(anchor.getFullYear()); setViewMonth(anchor.getMonth()) }
            } else if (defaultValue && typeof defaultValue === 'object' && !(defaultValue instanceof Date)) {
                const dv = defaultValue as ValueRangeObject
                const a = asDate(dv.firstDay)
                const b = asDate(dv.lastDay)
                setStart(a ?? null)
                setEnd(b ?? null)
                const anchor = a ?? b
                if (anchor) { setViewYear(anchor.getFullYear()); setViewMonth(anchor.getMonth()) }
            } else if (d0) {
                const { first, last } = getWeekRange(d0)
                setStart(first); setEnd(last)
                setViewYear(d0.getFullYear()); setViewMonth(d0.getMonth())
            }
        } else if (range) {
            if (Array.isArray(defaultValue) && defaultValue.length === 2) {
                const a = asDate(defaultValue[0])
                const b = asDate(defaultValue[1])
                setStart(a ?? null)
                setEnd(b ?? null)
                const anchor = a ?? b
                if (anchor) { setViewYear(anchor.getFullYear()); setViewMonth(anchor.getMonth()) }
            } else if (defaultValue && typeof defaultValue === 'object' && !(defaultValue instanceof Date)) {
                const dv = defaultValue as ValueRangeObject
                const a = asDate(dv.firstDay)
                const b = asDate(dv.lastDay)
                setStart(a ?? null)
                setEnd(b ?? null)
                const anchor = a ?? b
                if (anchor) { setViewYear(anchor.getFullYear()); setViewMonth(anchor.getMonth()) }
            } else if (d0) {
                setStart(d0); setEnd(null)
                setViewYear(d0.getFullYear()); setViewMonth(d0.getMonth())
            }
        } else {
            setSelectedDate(d0 ?? null)
            if (d0) {
                setViewYear(d0.getFullYear());
                setViewMonth(d0.getMonth())
                // Sync time values if includeTime is true
                if (includeTime) {
                    setSelectedHour(d0.getHours())
                    setSelectedMinute(d0.getMinutes())
                }
            }
        }
    }, [defaultValue, week, range, includeTime])

    const matrix = useMemo(() => getMonthMatrix(viewYear, viewMonth), [viewYear, viewMonth])

    const handleSelect = (day: Date) => {
        if (isDisabled(day, minDate, maxDate, disabledDates)) return

        // If clicked day is outside the currently viewed month/year, switch view
        if (day.getMonth() !== viewMonth || day.getFullYear() !== viewYear) {
            setViewMonth(day.getMonth())
            setViewYear(day.getFullYear())
        }

        if (week) {
            const { first, last } = getWeekRange(day)
            setStart(first)
            setEnd(last)
                ; (onChange as OnChangeRange)?.(
                    normalizeDateForSubmission(first),
                    normalizeDateForSubmission(last),
                    name
                )
            setOpen(false)
            return
        }

        if (range) {
            if (!start || (start && end)) {
                setStart(day)
                setEnd(null)
            } else {
                const a = start
                const b = day
                const first = new Date(Math.min(a.getTime(), b.getTime()))
                const last = new Date(Math.max(a.getTime(), b.getTime()))
                setStart(first)
                setEnd(last)
                    ; (onChange as OnChangeRange)?.(
                        normalizeDateForSubmission(first),
                        normalizeDateForSubmission(last),
                        name
                    )
                setOpen(false)
            }
            return
        }

        // Single day mode - apply time if includeTime is true
        const finalDate = new Date(day)
        if (includeTime) {
            finalDate.setHours(selectedHour, selectedMinute, 0, 0)
        }

        setSelectedDate(finalDate)
            ; (onChange as OnChangeSingle)?.(
                normalizeDateForSubmission(finalDate, { preserveTime: !!includeTime }),
                name
            )
        // Don't close popup for single date selection - let user close it manually or click outside
    }

    const goPrev = () => {
        const m = viewMonth - 1
        if (m < 0) { setViewMonth(11); setViewYear(viewYear - 1) } else { setViewMonth(m) }
    }
    const goNext = () => {
        const m = viewMonth + 1
        if (m > 11) { setViewMonth(0); setViewYear(viewYear + 1) } else { setViewMonth(m) }
    }

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => setViewMonth(Number(e.target.value))
    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => setViewYear(Number(e.target.value))

    const years = useMemo(() => {
        const current = new Date().getFullYear()
        const startYear = minDate ? minDate.getFullYear() : current - 70
        const endYear = maxDate ? maxDate.getFullYear() : current + 40
        const list: number[] = []
        for (let y = startYear; y <= endYear; y++) list.push(y)
        return list
    }, [minDate, maxDate])

    const convertDate = (date) => {
        const dt = new Date(date)
        return `${dt.getDate()}.${dt.getMonth() + 1}.${dt.getFullYear()}.`
    }

    const selectedLabel = useMemo(() => {
        if (week && start && end) return `${convertDate(start)} - ${convertDate(end)}`
        if (range && start && end) return `${convertDate(start)} - ${convertDate(end)}`
        if (!range && !week && selectedDate) {
            const dateStr = convertDate(selectedDate)
            if (includeTime) {
                const timeStr = `${selectedHour.toString().padStart(2, '0')}:${selectedMinute.toString().padStart(2, '0')}`
                return `${dateStr} ${timeStr}`
            }
            return dateStr
        }
        return placeholder ?? 'Select date'
    }, [week, range, start, end, selectedDate, placeholder, includeTime, selectedHour, selectedMinute])

    const hasValue = useMemo(() => {
        if (week || range) return !!start
        return !!selectedDate
    }, [week, range, start, selectedDate])

    return (
        <Wrapper width={width} ref={wrapperRef}>
            <InputWrapper nomarg={!labelText || nomarg} >
                {labelText && <Flex> <Label labelText={labelText} /> {required && <span style={{ color: 'red' }} >*</span>}</Flex>}
                <Display active={open} hasValue={hasValue} ref={refs.setReference as any} onClick={() => setOpen(s => !s)}>
                    {!hideIcon && <CalendarIcon />}
                    <Placeholder style={hasValue ? { color: "#fff" } : {}}  >{selectedLabel}</Placeholder>
                </Display>
            </InputWrapper>

            {open && createPortal(
                <Popup
                    ref={refs.setFloating as any}
                    fixed
                    rightAlign={false}
                    style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
                >
                    <Header>
                        <NavButton onClick={goPrev}>{'<'}</NavButton>
                        <Selects>
                            <select value={viewMonth} onChange={handleMonthChange}>
                                {months.map((m, i) => (
                                    <option key={m} value={i}>{m}</option>
                                ))}
                            </select>
                            <select value={viewYear} onChange={handleYearChange}>
                                {years.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </Selects>
                        <NavButton onClick={goNext}>{'>'}</NavButton>
                    </Header>

                    <WeekdaysRow>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                            <OtherText key={d} style={{ textAlign: 'center' }}>{d}</OtherText>
                        ))}
                    </WeekdaysRow>

                    <Grid>
                        {matrix.map((cell, idx) => {
                            const today = sameDay(cell, new Date())
                            const selected = (!range && !week && selectedDate && sameDay(cell, selectedDate)) || (range && start && sameDay(cell, start)) || (week && start && end && isBetween(cell, start, end))
                            const inRange = (range && isBetween(cell, start, end)) || (week && start && end && isBetween(cell, start, end))
                            const disabled = isDisabled(new Date(cell), minDate, maxDate, disabledDates)
                            const outside = cell.getMonth() !== viewMonth

                            return (
                                <DayCell
                                    key={idx}
                                    onClick={() => handleSelect(new Date(cell))}
                                    isToday={today}
                                    isSelected={!!selected}
                                    inRange={!!inRange}
                                    isDisabled={disabled}
                                    isOutside={outside}
                                    style={outside && !disabled ? { opacity: 0.4 } : undefined}
                                >
                                    <BodyText>{cell.getDate()}</BodyText>
                                </DayCell>
                            )
                        })}
                    </Grid>

                    {/* Time picker - only show for single day mode when includeTime is true */}
                    {includeTime && !week && !range && (
                        <TimePickerSection>
                            <TimePickerGroup>
                                <OtherText>Hour</OtherText>
                                <TimePickerSelect
                                    value={selectedHour}
                                    onChange={(e) => {
                                        const newHour = Number(e.target.value)
                                        setSelectedHour(newHour)
                                        // Update the selected date with new time and call onChange
                                        if (selectedDate) {
                                            const updatedDate = new Date(selectedDate)
                                            updatedDate.setHours(newHour, selectedMinute, 0, 0)
                                            setSelectedDate(updatedDate)
                                                ; (onChange as OnChangeSingle)?.(
                                                    normalizeDateForSubmission(updatedDate, { preserveTime: true }),
                                                    name
                                                )
                                        }
                                    }}
                                >
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <option key={i} value={i}>
                                            {i.toString().padStart(2, '0')}
                                        </option>
                                    ))}
                                </TimePickerSelect>
                            </TimePickerGroup>
                            <TimePickerGroup>
                                <OtherText>Minute</OtherText>
                                <TimePickerSelect
                                    value={selectedMinute}
                                    onChange={(e) => {
                                        const newMinute = Number(e.target.value)
                                        setSelectedMinute(newMinute)
                                        // Update the selected date with new time and call onChange
                                        if (selectedDate) {
                                            const updatedDate = new Date(selectedDate)
                                            updatedDate.setHours(selectedHour, newMinute, 0, 0)
                                            setSelectedDate(updatedDate)
                                                ; (onChange as OnChangeSingle)?.(
                                                    normalizeDateForSubmission(updatedDate, { preserveTime: true }),
                                                    name
                                                )
                                        }
                                    }}
                                >
                                    {Array.from({ length: 60 }, (_, i) => (
                                        <option key={i} value={i}>
                                            {i.toString().padStart(2, '0')}
                                        </option>
                                    ))}
                                </TimePickerSelect>
                            </TimePickerGroup>
                        </TimePickerSection>
                    )}
                </Popup>,
                document.body
            )}
        </Wrapper>
    )
}

export default CalendarInput


